
import { useState } from 'react'
import { TrendingUp, BarChart3, PieChart, Activity, DollarSign, Settings } from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { useGetDashboardAnalyticsQuery } from '@/store/api'
import DashboardMetricCard from '@/components/dashboard/DashboardMetricCard'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import ErrorMessage from '@/components/common/ErrorMessage'

export default function AnalyticsPage() {
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d' | '90d'>('30d')
  
  // Fetch analytics data
  const { data: analyticsData, isLoading, error, refetch } = useGetDashboardAnalyticsQuery({ timeframe })
  
  const metrics = analyticsData?.data?.metrics
  const trends = analyticsData?.data?.trends

  if (error) {
    // Error displayed to user via ErrorMessage component
    // Only log in development for debugging
    if (import.meta.env.DEV) {
      console.debug('Analytics API Error:', error)
    }
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
          <p className="text-lg text-gray-600">
            Performance metrics and insights
          </p>
        </div>
        <ErrorMessage 
          message="Failed to load analytics data" 
          onRetry={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
          <p className="text-lg text-gray-600">
            Performance metrics and insights for predictive maintenance
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-3">
          <select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
          <p className="ml-4 text-gray-600">Loading analytics data...</p>
        </div>
      ) : (
        <>
          {/* Debug Info */}
          <div className="bg-gray-100 p-4 rounded-lg text-xs">
            Debug: Loading={String(isLoading)}, Error={String(!!error)}, HasData={String(!!analyticsData)}, HasMetrics={String(!!metrics)}
          </div>
          
          {/* Key Metrics Overview */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Performance Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <DashboardMetricCard
                title="Total Alerts"
                value={metrics?.totalAlerts || 0}
                icon={Activity}
                iconColor="text-blue-600"
                iconBg="bg-blue-100"
                badge={{
                  text: `${metrics?.criticalAlerts || 0} Critical`,
                  variant: 'critical'
                }}
              />

              <DashboardMetricCard
                title="Active Work Orders"
                value={metrics?.activeWorkOrders || 0}
                icon={Settings}
                iconColor="text-orange-600"
                iconBg="bg-orange-100"
                badge={{
                  text: `${metrics?.emergencyWorkOrders || 0} Emergency`,
                  variant: 'warning'
                }}
              />

              <DashboardMetricCard
                title="Cost Savings"
                value={`$${((metrics?.costSavings || 0) / 1000).toFixed(1)}K`}
                icon={DollarSign}
                iconColor="text-green-600"
                iconBg="bg-green-100"
                badge={{
                  text: `${metrics?.predictiveAccuracy || 0}% Accuracy`,
                  variant: 'success'
                }}
              />

              <DashboardMetricCard
                title="Equipment Health"
                value={`${Math.round((metrics?.equipmentHealth?.good || 0) / (Object.values(metrics?.equipmentHealth || {}).reduce((a, b) => a + (b as number), 0) || 1) * 100)}%`}
                icon={TrendingUp}
                iconColor="text-purple-600"
                iconBg="bg-purple-100"
                badge={{
                  text: `${metrics?.equipmentHealth?.critical || 0} Critical`,
                  variant: metrics?.equipmentHealth?.critical ? 'critical' : 'success'
                }}
              />
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Alerts Trend */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Alert Trends</h3>
                  <BarChart3 className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  {trends?.alerts?.slice(-7).map((day) => (
                    <div key={day.date} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{new Date(day.date).toLocaleDateString()}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${(day.count / Math.max(...(trends?.alerts?.map(a => a.count) || [1]))) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{day.count}</span>
                        {day.critical > 0 && (
                          <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                            {day.critical} critical
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Work Orders Trend */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Work Order Trends</h3>
                  <PieChart className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                  {trends?.workOrders?.slice(-7).map((day) => (
                    <div key={day.date} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{new Date(day.date).toLocaleDateString()}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-orange-600 h-2 rounded-full" 
                            style={{ width: `${(day.count / Math.max(...(trends?.workOrders?.map(w => w.count) || [1]))) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 w-8">{day.count}</span>
                        {day.completed > 0 && (
                          <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                            {day.completed} done
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Equipment Health Distribution */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Equipment Health Distribution</h3>
                <TrendingUp className="w-5 h-5 text-gray-400" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(metrics?.equipmentHealth || {}).map(([status, count]) => (
                  <div key={status} className="text-center">
                    <div className={`text-2xl font-bold ${
                      status === 'excellent' ? 'text-green-600' :
                      status === 'good' ? 'text-blue-600' :
                      status === 'warning' ? 'text-yellow-600' :
                      status === 'poor' ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {count as number}
                    </div>
                    <div className="text-xs text-gray-600 uppercase tracking-wide">
                      {status}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resolution Performance</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Resolution Time</span>
                    <span className="font-medium">{metrics?.avgResolutionTime || 0}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed Work Orders</span>
                    <span className="font-medium">{metrics?.completedWorkOrders || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Predictive Accuracy</span>
                    <span className="font-medium text-green-600">{metrics?.predictiveAccuracy || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Analysis</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Maintenance Costs</span>
                    <span className="font-medium">${(metrics?.maintenanceCosts || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Planned Work</span>
                    <span className="font-medium">{metrics?.plannedVsUnplanned?.planned || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Unplanned Work</span>
                    <span className="font-medium text-orange-600">{metrics?.plannedVsUnplanned?.unplanned || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Equipment</span>
                    <span className="font-medium">{Object.values(metrics?.equipmentHealth || {}).reduce((a, b) => a + (b as number), 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Critical Alerts</span>
                    <span className="font-medium text-red-600">{metrics?.criticalAlerts || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Emergency Orders</span>
                    <span className="font-medium text-orange-600">{metrics?.emergencyWorkOrders || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}